import type { Scene } from '../types'

export const solutionScenes: Scene[] = [
  {
    id: 'SOL-01',
    chapter: 'solution',
    route: '/solution/overview',
    title: 'Product Concept',
    headline: 'Designed for SRWMD, not adapted to SRWMD.',
    supporting:
      'A purpose-built web application is explicitly acceptable under the District\'s Questions & Answers. LAMS uses that flexibility to model SRWMD\'s land lifecycle directly, while preserving integration with the District\'s existing enterprise environment.',
    visual: 'product-concept',
    whyItMatters:
      'A configured commercial product would require the District\'s land process to be expressed in someone else\'s data model. Building to the District\'s lifecycle removes that translation layer — and the permanent cost of maintaining it.',
    sourceRefs: [
      { kind: 'qa', label: 'Source: SRWMD Questions & Answers', locator: 'Q13, p.2' },
      { kind: 'proposal', label: 'Merlion Proposed Solution', locator: 'Section 2' },
    ],
    presenterNotes: [
      'Lead with the District\'s own clarification. A proposer asked whether a purpose-built custom web application would be considered; the District answered that it would.',
      'This is the decision the whole architecture rests on. Make it explicit that the District authorized it.',
      'Do not disparage COTS products. The argument is fit, not quality.',
    ],
    estimatedSeconds: 60,
    rfpAlignment: ['Section 5 — Scope of Work'],
    evalTags: ['EVAL-WP'],
    exploreDetails: [
      {
        title: 'The District\'s clarification',
        body: [
          'Question 13 asked: Section 5 requests a "customized off the shelf software solution." Would the District also consider a purpose-built custom web application developed specifically to the District\'s requirements, or is a configured commercial off-the-shelf product required?',
          'The District answered: "Purpose-built custom web application developed specifically to the District\'s requirements would be considered."',
        ],
        sources: [{ kind: 'qa', label: 'SRWMD Questions & Answers', locator: 'Q13, p.2' }],
      },
    ],
  },

  {
    id: 'SOL-02',
    chapter: 'solution',
    route: '/solution/organization',
    title: 'Product Organization',
    headline: 'LAMS Operations organizes work around business context.',
    supporting:
      'Nine operational areas, each entered from the business object a District officer is actually working on — not from a technical menu.',
    visual: 'application-map',
    sourceRefs: [
      { kind: 'architecture', label: 'Merlion Proposed Architecture', locator: 'Operations Business Architecture, Sections 3, 7, 9' },
      { kind: 'proposal', label: 'Merlion Proposed Solution', locator: 'Section 3 — Product Backlog' },
    ],
    presenterNotes: [
      'Walk the map once, briefly. Do not describe every application.',
      'Note the layer underneath — Studio and Runtime — but say it will be explained in the architecture chapter. Do not explain it here.',
      'The point of this scene is that the product has a coherent shape, not that it has many features.',
    ],
    estimatedSeconds: 60,
    rfpAlignment: ['Section 5.A — Shared Database Capabilities'],
    evalTags: ['EVAL-WP'],
  },

  {
    id: 'SOL-03',
    chapter: 'solution',
    route: '/solution/property',
    title: 'Property 360°',
    headline: 'A Property is more than a database record.',
    supporting:
      'Property becomes the durable managed land context connecting location, classification, measurements, lifecycle records, work, documents and spatial information.',
    visual: 'property-workspace',
    conceptualLabel: true,
    callouts: [
      { label: 'Overview' },
      { label: 'Location' },
      { label: 'GIS' },
      { label: 'Classification' },
      { label: 'Measures' },
      { label: 'Documents' },
      { label: 'Work' },
      { label: 'History' },
    ],
    whyItMatters:
      'Every question an evaluator might ask about a tract — where is it, what is it, what has been measured on it, what work has been done, what documents exist, what happened and when — is answered from one place.',
    sourceRefs: [
      { kind: 'architecture', label: 'Merlion Proposed Architecture', locator: 'Operations Business Architecture, Sections 8, 13' },
      { kind: 'architecture', label: 'Merlion Proposed Architecture', locator: 'Frontend Architecture, Property Workspace' },
    ],
    presenterNotes: [
      'This is the single most important product screen. Spend time here.',
      'Make the continuity argument concrete: this same record is what Acquisition created and what Disposition will later read.',
      'State clearly that this is a conceptual design, not a screenshot of a finished production system.',
    ],
    estimatedSeconds: 90,
    rfpAlignment: ['Section 5.A — Shared Database Capabilities', 'Section 5.C — Land Management'],
    evalTags: ['EVAL-WP'],
  },

  {
    id: 'SOL-04',
    chapter: 'solution',
    route: '/solution/acquisition',
    title: 'Acquisition Journey',
    headline: 'From intake to closing, with the critical path visible.',
    supporting:
      'The RFP asks for file number assignment, GIS triage maps, prospectus tools, LAS ranking support, due-diligence tracking and transition to Land Management. LAMS delivers them as one connected path.',
    visual: 'acquisition-flow',
    callouts: [
      { label: 'GIS triage', detail: 'Triage map creation from GIS data or API' },
      { label: 'Land Management Prospectus', detail: 'Site inspection and program planning with financial estimates' },
      { label: 'LAS ranking', detail: 'Score sheet generation and memorandum preparation' },
      { label: 'Due diligence', detail: 'Appraisals, ESAs, contracts, memos, closing documents' },
      { label: 'Transition', detail: 'Applicable data carried into Land Management' },
    ],
    sourceRefs: [
      { kind: 'rfp', label: 'Source: SRWMD RFP 26/27-002', locator: 'Section 5.B, p.7' },
      { kind: 'proposal', label: 'Merlion Proposed Solution', locator: 'Section 3.2 — Backlog LA-01 to LA-08' },
    ],
    presenterNotes: [
      'Follow the path left to right and name the District role at each step.',
      'The prospectus is the step that connects acquisition to land management — it is where the future stewardship plan is first written down. Flag it.',
      'Due diligence is where the District currently loses time to manual prompting. Emphasize task management here.',
    ],
    estimatedSeconds: 90,
    rfpAlignment: [
      'Section 5.B — Workflow Automation',
      'Section 5.B — Resource Planning',
      'Section 5.B — Approval Management',
      'Section 5.B — Due Diligence Tracking',
      'Section 5.B — Transitioning Modules',
    ],
    evalTags: ['EVAL-WP'],
    exploreDetails: [
      {
        title: 'Acquisition capabilities against the RFP',
        table: {
          head: ['RFP requirement (Section 5.B)', 'LAMS capability'],
          rows: [
            [
              'Workflow Automation — file number assignment, triage map creation based on GIS data or API',
              'Automated file numbering on intake; GIS triage map generated against District ArcGIS services and attached to the acquisition record.',
            ],
            [
              'Resource Planning — Land Management Prospectus prior to acquisition',
              'Structured prospectus combining site inspection, program planning and financial estimates, carried forward into the three-year plan after acquisition.',
            ],
            [
              'Approval Management — LAS team ranking, score sheet generation, memoranda for Committee and Board',
              'Systemic ranking support with generated score sheets and templated Committee and Board memoranda.',
            ],
            [
              'Due Diligence Tracking — appraisals, ESAs, contracts, memos, closing documents',
              'Critical-path task management with prompts, responsible parties and due dates; documents linked to the acquisition record and to PaperVision.',
            ],
            [
              'Transitioning Modules — transition applicable data to Land Management',
              'The Property context, classification, attributes, measures and document references persist; Land Management continues on the same record.',
            ],
          ],
        },
        sources: [
          { kind: 'rfp', label: 'SRWMD RFP 26/27-002', locator: 'Section 5.B, p.7' },
          { kind: 'proposal', label: 'Merlion Proposed Solution', locator: 'Section 3.2' },
        ],
      },
    ],
  },

  {
    id: 'SOL-05',
    chapter: 'solution',
    route: '/solution/management',
    title: 'Land Management Journey',
    headline: 'Turn stewardship plans into traceable operational work.',
    supporting:
      'Three-year plans become scheduled, assigned, executed and inspected work — associated with tracts, regions, contracts and purchase orders, and recorded against the same Property.',
    visual: 'management-flow',
    callouts: [
      { label: 'Boundary maintenance' },
      { label: 'Prescribed fire & vegetation' },
      { label: 'Roads, mowing & daylighting' },
      { label: 'Exotic plants & rare species' },
      { label: 'Archaeology & facilities' },
      { label: 'Timber lifecycle' },
    ],
    whyItMatters:
      'The District plans in GIS attribute tables by program area and executes through staff and contractors. Connecting the plan to the work to the record is what makes annual reporting a query instead of a spreadsheet exercise.',
    sourceRefs: [
      { kind: 'rfp', label: 'Source: SRWMD RFP 26/27-002', locator: 'Section 5.C, pp.7-8' },
      { kind: 'proposal', label: 'Merlion Proposed Solution', locator: 'Section 3.3 — Backlog LM-01 to LM-08' },
    ],
    presenterNotes: [
      'This is the largest domain and the District\'s daily reality. Give it the most time in this chapter.',
      'Name the actual programs from the RFP — prescribed fire, exotic plant management, rare species monitoring, archaeological monitoring. Using the District\'s own program vocabulary demonstrates we read Section 5.C.',
      'Timber is a distinct lifecycle with its own requirements: pre-harvest meetings, sale records, load tracking, closeouts with ratings, site preparation and reforestation cycles. Mention it explicitly.',
    ],
    estimatedSeconds: 100,
    rfpAlignment: [
      'Section 5.C — Resource Planning, 3-year plans with GIS bi-directional communication',
      'Section 5.C — Operational Task Management',
      'Section 5.C — Natural Resource Oversight, timber management',
      'Section 5.C — Association to contracts, purchase orders, tracts, facilities, regions',
      'Section 5.C — Automation of memos, contracts, bids and letters',
    ],
    evalTags: ['EVAL-WP'],
    exploreDetails: [
      {
        title: 'Operational task management scope',
        body: [
          'The RFP names the tasks the District needs scheduled, assigned and tracked: boundary maintenance, vegetative management (prescribed fire, chemical and mechanical), road maintenance, road mowing, ditch and daylight mowing, exotic plant management, rare species monitoring, archaeological monitoring, facility improvements, and recreational maintenance.',
          'Each is scheduled against a program plan, assigned to staff or a contractor, executed, inspected and recorded — with linked associations for tracts, regions, contracts and purchase orders.',
        ],
        sources: [{ kind: 'rfp', label: 'SRWMD RFP 26/27-002', locator: 'Section 5.C, pp.7-8' }],
      },
      {
        title: 'Timber lifecycle',
        body: [
          'The RFP requires specialized tracking for timber management including pre-harvest meeting documentation, sale records, financial activity, inspections, load tracking, inventory updates, closeouts with ratings, the site preparation planning cycle, and the reforestation planning cycle.',
          'LAMS models this as a distinct lifecycle against the Property, so a stand harvested in one year and replanted in the next remains one continuous record.',
        ],
        sources: [
          { kind: 'rfp', label: 'SRWMD RFP 26/27-002', locator: 'Section 5.C — Natural Resource Oversight' },
          { kind: 'proposal', label: 'Merlion Proposed Solution', locator: 'Section 3.3 — LM-06' },
        ],
      },
    ],
  },

  {
    id: 'SOL-06',
    chapter: 'solution',
    route: '/solution/disposition',
    title: 'Disposition Journey',
    headline: 'Disposition continues the land record — it does not restart it.',
    supporting:
      'Everything accumulated during acquisition and stewardship — classification, attributes, measure history, documents, work — is already present when a disposition evaluation begins.',
    visual: 'disposition-flow',
    sourceRefs: [
      { kind: 'rfp', label: 'Source: SRWMD RFP 26/27-002', locator: 'Section 5.D, p.8' },
      { kind: 'proposal', label: 'Merlion Proposed Solution', locator: 'Section 3.4 — Backlog LD-01 to LD-06' },
    ],
    presenterNotes: [
      'Short scene. The argument was already made in CH-04; this is the payoff.',
      'Point out that the RFP asks for data to transition from Land Management into Disposition — and that with a single Property record there is no transition step to get wrong.',
    ],
    estimatedSeconds: 60,
    rfpAlignment: [
      'Section 5.D — Workflow Automation, triage map creation',
      'Section 5.D — Approval Management, LAS approval and Board memoranda',
      'Section 5.D — Due Diligence Tracking',
      'Section 5.D — Transitioning Modules',
    ],
    evalTags: ['EVAL-WP'],
  },

  {
    id: 'SOL-07',
    chapter: 'solution',
    route: '/solution/gis',
    title: 'GIS as Operational Context',
    headline: 'GIS is part of the work — not a separate destination.',
    supporting:
      'Users keep the business record and the geospatial context visible at the same time. GIS can be a dedicated workspace or an embedded capability inside any workflow.',
    visual: 'gis-snap-layout',
    conceptualLabel: true,
    whyItMatters:
      'The RFP calls GIS integration "required" and names it GIS Centrality. Making geography a tab the user leaves the record to visit would reproduce exactly the context-switching the District is trying to remove.',
    sourceRefs: [
      { kind: 'rfp', label: 'Source: SRWMD RFP 26/27-002', locator: 'Section 5.E — GIS Centrality, p.8' },
      { kind: 'architecture', label: 'Merlion Proposed Architecture', locator: 'Studio Architecture, Sections 12-15' },
      { kind: 'architecture', label: 'Merlion Proposed Architecture', locator: 'Frontend Architecture, GIS Workspace and Snap Layout' },
    ],
    presenterNotes: [
      'Demonstrate the split: record on one side, map on the other, one context.',
      'Be precise about the boundary — the District maintains parcel geometry in ArcGIS. LAMS does not edit parcel geometry. This was clarified in the Q&A and we build to it.',
      'ArcGIS remains the District GIS ecosystem. LAMS is a consumer, not a replacement.',
    ],
    estimatedSeconds: 75,
    rfpAlignment: ['Section 5.E — GIS Centrality'],
    evalTags: ['EVAL-WP'],
    exploreDetails: [
      {
        title: 'Where the GIS boundary sits',
        bullets: [
          'The District continues to maintain parcel boundaries and natural community layers in its own ArcGIS environment.',
          'LAMS does not require direct editing of parcel geometry as part of the proposed GIS operating model.',
          'LAMS owns spatial attributes only where the business object itself is the thing being located.',
          'ArcGIS Field Maps, Collector and Survey123 remain authoritative for the District\'s field data collection workflows; LAMS integrates the resulting data rather than replacing those applications.',
          'Compatibility with ArcGIS Server 11.1 and ArcGIS Pro 3.4 is a District requirement and is part of the baseline.',
        ],
        sources: [
          { kind: 'qa', label: 'SRWMD Questions & Answers', locator: 'Q5, Q40' },
          { kind: 'proposal', label: 'Merlion Proposed Architecture', locator: 'Section 6.3' },
        ],
      },
    ],
  },

  {
    id: 'SOL-08',
    chapter: 'solution',
    route: '/solution/work-documents',
    title: 'Work, Documents and Traceability',
    headline: 'Work stays connected to the land context that created it.',
    supporting:
      'A task carries its responsible party, due date, related property or case, its documents and its full workflow history — because it was created from the record, not in a separate tracker.',
    visual: 'work-task-workspace',
    conceptualLabel: true,
    whyItMatters:
      'The RFP asks for automated creation of memos, contracts, bids and letters in both Section 5.A and Section 5.C. Generating a document from the record that holds the facts is what makes the document trustworthy.',
    sourceRefs: [
      { kind: 'rfp', label: 'Source: SRWMD RFP 26/27-002', locator: 'Sections 5.A, 5.B, 5.C' },
      { kind: 'architecture', label: 'Merlion Proposed Architecture', locator: 'Frontend Architecture, Work reference' },
      { kind: 'proposal', label: 'Merlion Proposed Solution', locator: 'Section 3.6 — Document Generation' },
    ],
    presenterNotes: [
      'Connect to the District\'s current pain: documents routed manually through Windows network folders.',
      'Document generation is a scored capability the District named twice in the scope. Do not skip it.',
      'Mention that generated documents carry the accessibility obligations under Section 508 and Florida Statute 282.603.',
    ],
    estimatedSeconds: 70,
    rfpAlignment: [
      'Section 5.A — Automation, create memos, contracts, bids and letters',
      'Section 5.B — Due Diligence Tracking',
      'Section 5.C — Automation',
    ],
    evalTags: ['EVAL-WP', 'EVAL-OUTCOME'],
  },

  {
    id: 'SOL-09',
    chapter: 'solution',
    route: '/solution/experience',
    title: 'Desktop + Mobile Experience',
    headline: 'Desktop productivity. Mobile-compatible access.',
    supporting:
      'LAMS is desktop-first for GIS-heavy and multi-context work, while remaining responsive and mobile compatible. Native iOS and Android applications are not required by the District.',
    visual: 'desktop-mobile',
    conceptualLabel: true,
    whyItMatters:
      'Building a native mobile application the District did not ask for would add cost, a second release cycle and an app-store dependency — for a user base of roughly twenty people who work primarily at a desk.',
    sourceRefs: [
      { kind: 'rfp', label: 'Source: SRWMD RFP 26/27-002', locator: 'Section 5.A — mobile compatibility, p.6' },
      { kind: 'qa', label: 'Source: SRWMD Questions & Answers', locator: 'Q112, p.13; Q61, p.8' },
      { kind: 'architecture', label: 'Merlion Proposed Architecture', locator: 'Frontend Architecture, Mobile Experience Architecture' },
    ],
    presenterNotes: [
      'Be exact: the District answered "Native applications are not required." Do not claim a native app, and do not apologize for not having one.',
      'The District also answered "No" when asked whether offline data capture with later synchronization is required. Say so — it shows we read the answers.',
      'Field data collection continues in the District\'s existing ArcGIS field tools. LAMS integrates that data.',
    ],
    estimatedSeconds: 60,
    rfpAlignment: ['Section 5.A — Integration, mobile compatibility needed'],
    evalTags: ['EVAL-WP'],
    exploreDetails: [
      {
        title: 'What the District clarified about mobile',
        table: {
          head: ['Question', 'District answer'],
          rows: [
            [
              'Q112 — Is mobile compatibility satisfied by a responsive web interface usable on tablets and smartphones, or are native iOS/Android applications expected?',
              '"Native applications are not required."',
            ],
            [
              'Q61 — Is offline data capture with later synchronization required for the mobile-compatible components?',
              '"No."',
            ],
            [
              'Q33 — Are specific mobile platforms, native app versus responsive web, and offline capture required?',
              '"No."',
            ],
          ],
        },
        sources: [{ kind: 'qa', label: 'SRWMD Questions & Answers', locator: 'Q33, Q61, Q112' }],
      },
      {
        title: 'Consequences for the proposed solution',
        bullets: [
          'One responsive web application, one release cycle, one codebase to maintain and secure.',
          'No specialized end-user hardware or dedicated desktop application is required beyond a supported browser.',
          'Desktop layouts are optimized for GIS-heavy and multi-context work, including side-by-side record and map.',
          'Tablet and phone layouts support review, task handling and record lookup away from the desk.',
        ],
        sources: [{ kind: 'proposal', label: 'Merlion Proposed Architecture', locator: 'Sections 6.1, 6.5' }],
      },
    ],
  },
]
